import { z } from 'zod';
import { FIELD_LIMITS, MAX_CHOICES_PER_QUESTION, MAX_QUESTIONS_PER_JOB } from './constants.js';
import { isChoiceQuestionType, questionTypeSchema, type QuestionType } from './enums.js';

const choiceSchema = z.string().trim().min(1).max(FIELD_LIMITS.questionChoice);

const questionBaseSchema = z.object({
  label: z.string().trim().min(1, 'Label is required').max(FIELD_LIMITS.questionLabel),
  helpText: z
    .string()
    .trim()
    .max(FIELD_LIMITS.questionHelpText)
    .nullish()
    .transform((v) => v || null),
  type: questionTypeSchema,
  required: z.boolean().default(false),
  active: z.boolean().default(true),
  choices: z.array(choiceSchema).max(MAX_CHOICES_PER_QUESTION).default([]),
});

/**
 * Choice lists only make sense for select types, and a select without options is
 * an unanswerable question — so both directions are rejected rather than coerced.
 */
function refineChoices<T extends { type: QuestionType; choices: string[] }>(
  value: T,
  ctx: z.RefinementCtx,
): void {
  if (isChoiceQuestionType(value.type)) {
    if (value.choices.length < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['choices'],
        message: 'Add at least one choice for a select question',
      });
    }
    const seen = new Set<string>();
    value.choices.forEach((choice, index) => {
      const key = choice.toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', path: ['choices', index], message: 'Choices must be unique' });
      }
      seen.add(key);
    });
  } else if (value.choices.length > 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['choices'],
      message: 'Only select questions can define choices',
    });
  }
}

export const questionInputSchema = questionBaseSchema.superRefine(refineChoices);
export type QuestionInput = z.infer<typeof questionInputSchema>;

export const questionUpdateSchema = questionBaseSchema.superRefine(refineChoices);
export type QuestionUpdate = z.infer<typeof questionUpdateSchema>;

export const questionReorderSchema = z.object({
  questionIds: z.array(z.uuid()).min(1).max(MAX_QUESTIONS_PER_JOB),
});
export type QuestionReorder = z.infer<typeof questionReorderSchema>;

/** A question as returned by the API. */
export interface JobQuestionDto {
  id: string;
  label: string;
  helpText: string | null;
  type: QuestionType;
  required: boolean;
  active: boolean;
  sortOrder: number;
  choices: string[];
}

/** Question definition frozen into an application at submission time. */
export interface QuestionSnapshot {
  label: string;
  type: QuestionType;
  required: boolean;
  choices: string[];
}
